import React, { useEffect, useState } from "react";
import { ArrowLeft, BadgeCheck, MapPin, Star, Store } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { avatarColor, getInitials } from "@/lib/avatar";
import { useI18n } from "@/context/I18nContext";
import ProductCard from "@/components/market/ProductCard";

export default function Storefront({ shopId, currency, onBack, onOpenProduct, onMessage }) {
  const { t } = useI18n();
  const [shop, setShop] = useState(null);
  useEffect(() => { setShop(null); api.get(`/market/shops/${shopId}`).then(({ data }) => setShop(data)).catch(() => {}); }, [shopId]);
  if (!shop) return <div className="p-8 text-center text-[var(--rc-muted)]">{t("m_loading")}</div>;
  const products = shop.products || [];

  return (
    <div className="pb-24" data-testid="storefront">
      <button onClick={onBack} data-testid="storefront-back" className="flex items-center gap-1.5 text-[var(--rc-muted)] mb-3"><ArrowLeft size={18} /> {t("s_back")}</button>

      <div className="rounded-2xl overflow-hidden border border-[var(--rc-border)] bg-[var(--rc-surface)]">
        <div className="h-32 bg-[var(--rc-bg)]">
          {shop.banner ? <img src={fileUrl(shop.banner)} alt="" className="h-full w-full object-cover" data-testid="storefront-banner" /> : <div className="h-full w-full bg-gradient-to-br from-[var(--rc-gold)]/25 to-transparent" />}
        </div>
        <div className="px-4 pb-4">
          <div className="-mt-9 flex items-end gap-3">
            <div className="h-[72px] w-[72px] rounded-full overflow-hidden grid place-items-center border-4 border-[var(--rc-surface)] text-[var(--rc-gold)] text-xl font-semibold"
              style={{ backgroundColor: shop.avatar ? "var(--rc-bg)" : avatarColor(shop.name) }} data-testid="storefront-avatar">
              {shop.avatar ? <img src={fileUrl(shop.avatar)} alt="" className="h-full w-full object-cover" /> : getInitials(shop.name)}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <h2 className="text-xl font-medium">{shop.name}</h2>
            {shop.verified && <BadgeCheck size={18} className="text-[var(--rc-gold)]" data-testid="storefront-verified" />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[var(--rc-muted)]">
            {shop.rating_count > 0 && <span className="flex items-center gap-1 text-[var(--rc-gold)]"><Star size={13} className="fill-[var(--rc-gold)]" /> {shop.rating_avg} ({shop.rating_count})</span>}
            {shop.location && <span className="flex items-center gap-1"><MapPin size={13} /> {shop.location}</span>}
            <span className="flex items-center gap-1"><Store size={13} /> {shop.product_count} {t("m_shop_items")}</span>
          </div>
          {shop.description && <p className="text-sm text-[var(--rc-muted)] mt-2 [overflow-wrap:anywhere]">{shop.description}</p>}
        </div>
      </div>

      <div className="mt-5">
        {products.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="storefront-products">
            {products.map((p) => <ProductCard key={p.id} product={p} currency={currency} onMessage={onMessage} onOpen={(prod) => onOpenProduct(prod.id)} />)}
          </div>
        ) : <div className="text-center py-12 text-[var(--rc-muted)]">{t("m_shop_no_items")}</div>}
      </div>
    </div>
  );
}
